import React, { useState } from "react";
import CountryCodePicker from "./CountryCodePicker";

import Timer from "otp-timer";
import { toast } from "sonner";
import {
  searchAbhaPhone,
  abhaLogin,
  loginOtpVerify,
  getAbhaProfile,
  getAbhaAuthMethods,
  abhaLoginUsingAddress,
  abhaAddressOtpVerify,
  createAbha,
  abhaCreateOtpVerify,
  createAbhaAddress,
} from "../api/abha";
import aadhaar from "../assets/images/aadhaar.png";
import mobile from "../assets/images/mobile.png";
import { Icon } from "@iconify/react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import OtpInput from './Reusables/FormControls/FormOtpVerify';

const modalShellSx = {
  p: { xs: 2, sm: 2.5 },
  color: "var(--app-text)",
  backgroundColor: "var(--app-surface)",
  borderRadius: "14px",
};

const cardSx = {
  border: "1px solid var(--app-border)",
  borderRadius: "12px",
  backgroundColor: "var(--app-surface)",
  boxShadow: "var(--app-shadow)",
};

const mutedTextSx = {
  color: "var(--app-text-muted)",
  fontSize: 14,
  lineHeight: 1.6,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    backgroundColor: "var(--app-surface)",
    color: "var(--app-text)",
    "& fieldset": { borderColor: "var(--app-border)" },
    "&:hover fieldset": { borderColor: "var(--app-text-muted)" },
    "&.Mui-focused fieldset": {
      borderColor: "#5751E9",
      borderWidth: 1,
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--app-surface-2)",
    },
  },
  "& .MuiInputBase-input": {
    color: "var(--app-text)",
    fontFamily: "Figtree",
  },
};

const primaryButtonSx = {
  height: 48,
  borderRadius: "9px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 16,
  backgroundColor: "#5751E9",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#4a43d6",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "var(--app-border)",
    color: "var(--app-text-muted)",
  },
};

const ghostButtonSx = {
  p: 0,
  minWidth: 0,
  color: "#5751E9",
  textTransform: "none",
  fontWeight: 700,
  verticalAlign: "baseline",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
};

const ModalShell = ({ title, children, onBack, onClose, action, dense = false }) => (
  <Box sx={modalShellSx}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        mb: dense ? 2 : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {onBack && (
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="Back"
            sx={{
              color: "var(--app-text)",
              border: "1px solid var(--app-border)",
              backgroundColor: "var(--app-surface-2)",
            }}
          >
            <Icon icon="mdi:chevron-left" width={22} height={22} />
          </IconButton>
        )}
        <Typography
          variant="h6"
          noWrap
          sx={{ color: "var(--app-text)", fontWeight: 800, fontSize: 20 }}
        >
          {title}
        </Typography>
      </Box>
      {action || (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ color: "var(--app-text-muted)" }}
        >
          <Icon icon="mdi:close" width={22} height={22} />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
);

const OptionCard = ({ selected, label, subtitle, icon, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") onClick?.();
    }}
    sx={{
      ...cardSx,
      p: 2,
      cursor: "pointer",
      backgroundColor: selected ? "var(--app-hover)" : "var(--app-surface-2)",
      borderColor: selected ? "#5751E9" : "var(--app-border)",
      transition: "border-color 160ms ease, background-color 160ms ease",
      "&:hover": {
        borderColor: "#5751E9",
        backgroundColor: "var(--app-hover)",
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {icon && (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            color: selected ? "#5751E9" : "var(--app-text-muted)",
            backgroundColor: selected ? "#EEEEFF" : "var(--app-surface)",
            flex: "0 0 auto",
          }}
        >
          <Icon icon={icon} width={22} height={22} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 700 }}>
          {label}
        </Typography>
        {subtitle && <Typography sx={mutedTextSx}>{subtitle}</Typography>}
      </Box>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          border: selected ? "none" : "1px solid var(--app-border)",
          backgroundColor: selected ? "#068F06" : "var(--app-surface)",
          color: "#fff",
        }}
      >
        {selected && <Icon icon="mdi:check" width={15} height={15} />}
      </Box>
    </Box>
  </Box>
);

const AuthMethodCard = ({ selected, label, image, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ...cardSx,
      p: 2,
      textAlign: "center",
      cursor: "pointer",
      backgroundColor: selected ? "var(--app-hover)" : "var(--app-surface)",
      borderColor: selected ? "#5751E9" : "var(--app-border)",
      "&:hover": {
        borderColor: "#5751E9",
        backgroundColor: "var(--app-hover)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        mx: "auto",
        mb: 1,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        backgroundColor: "var(--app-surface-2)",
      }}
    >
      <img src={image} alt={label} style={{ width: 36, height: 36, objectFit: "contain" }} />
    </Box>
    <Typography sx={{ color: "var(--app-text)", fontWeight: 800, fontSize: 14 }}>
      {label}
    </Typography>
  </Box>
);

const AbhaVerificationModal = ({ onClose, updateAbhaUser, existingPatient }) => {
  const [selectedOption, setSelectedOption] = useState("phone");
  const [inputValue, setInputValue] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [currentStep, setCurrentStep] = useState("selection"); // "selection", "verification", "linkedAccounts", "otp", "abhaCard", "createAadhaar", "createOtp", "createAddress", "createExistingAccounts"
  const [otp, setOtp] = useState("");
  const [maskedNumber, setMaskedNumber] = useState("");
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false); // For Verify OTP (login)
  const [createFlow, setCreateFlow] = useState(false); // true for create flow, false for login flow
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [abhaAddress, setAbhaAddress] = useState("");
  const [selectedSuggestedAddress, setSelectedSuggestedAddress] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState("");
  const [createOtpLoading, setCreateOtpLoading] = useState(false); // For sending OTP in create flow
  const [createVerifyOtpLoading, setCreateVerifyOtpLoading] = useState(false); // For verifying OTP in create flow
  const [createMobileNumber, setCreateMobileNumber] = useState("");
  const [linkedProfiles, setLinkedProfiles] = useState([]);
  const [selectedLinkedProfileId, setSelectedLinkedProfileId] = useState(null);
  const [abhaValidateMethod, setAbhaValidateMethod] = useState("aadhaar"); // 'aadhaar' | 'mobile'
  const [otpTargetMessage, setOtpTargetMessage] = useState("");
  const [addressAuthMethods, setAddressAuthMethods] = useState([]);
  const [selectedAddressAuthMethod, setSelectedAddressAuthMethod] = useState("");
  const [addressAuthLoading, setAddressAuthLoading] = useState(false); // For addressAuth "Get OTP"
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false); // For "Get OTP" in login flows
  const [checkedTerms, setCheckedTerms] = useState({
    term1: false,
    term2: false,
    term3: false,
    term3: false,
  });
  const [txn_id, setTxn_id] = useState("");
  const [createAddressLoading, setCreateAddressLoading] = useState(false);
  const [abhaProfile, setAbhaProfile] = useState(null);
  const [token, setToken] = useState("");

  const formatAbhaNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "").slice(0, 18);
    const groups = [];
    const sizes = [2, 4, 4, 4, 4];
    let index = 0;
    for (let i = 0; i < sizes.length && index < digits.length; i++) {
      const size = sizes[i];
      const next = digits.slice(index, index + size);
      if (next.length) groups.push(next);
      index += size;
    }
    return groups.join("-");
  };

  const formatAadhaarNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "").slice(0, 12);
    const groups = [];
    const sizes = [4, 4, 4];
    let index = 0;
    for (let i = 0; i < sizes.length && index < digits.length; i++) {
      const size = sizes[i];
      const next = digits.slice(index, index + size);
      if (next.length) groups.push(next);
      index += size;
    }
    return groups.join("-");
    return groups.join("-");
  };

  const handleOptionChange = (option) => {
    setSelectedOption(option);
    setInputValue("");
    setOtp("");
  };

  const handleContinue = async () => {
    if (currentStep === "selection") {
      setCurrentStep("verification");
      return;
    }

    if (currentStep === "verification") {
      if (!inputValue.trim()) return;

      if (selectedOption === "phone") {
        // Phone flow: search linked ABHA profiles, then show list
        try {
          const phone = inputValue.replace(/\D/g, "");
          const masked = phone.replace(/\d(?=\d{4})/g, "X");
          setMaskedNumber(masked);
          setOtpLoading(true);
          if (existingPatient?.phone && existingPatient.phone !== `+91${phone}`) {
            toast.error("Phone number does not match the existing patient.");
            return;
          }

          const response = await searchAbhaPhone({ mobile: phone });

          if (response?.success) {
            setTxn_id(response?.data?.txnId);
            const profiles = (response?.data?.ABHA ?? []).map((item) => ({
              name: item?.name ?? "",
              abhaNumber: item?.ABHANumber ?? "",
              index: item?.index ?? null,
              gender: item?.gender ?? "",
              kycVerified: item?.kycVerified ?? false,
            }));
            setLinkedProfiles(profiles);
          } else {
            setLinkedProfiles([]);
            if (response?.message) toast.error(response.message);
          }
          setCurrentStep("linkedAccounts");
        } catch (error) {
          console.error(error);
          toast.error("Unable to fetch linked profiles. Please try again.");
        } finally {
          setOtpLoading(false);
        }
        return;
      }

      // Non-phone flows
      if (selectedOption === "abha") {
        try {
          if (existingPatient?.abhaNumber && existingPatient?.abhaNumber !== inputValue) {
            toast.error("ABHA number does not match the existing patient.");
            return;
          }
          setOtpLoading(true);
          const response = await abhaLogin({
            abhaNumber: inputValue,
            mobileOtp: abhaValidateMethod === "aadhaar" ? false : true,
          });

          if (response?.success) {
            setTxn_id(response?.txnId);
            setOtpTargetMessage(
              abhaValidateMethod === "aadhaar"
                ? "your Aadhaar-linked mobile number"
                : "your registered mobile number"
            );
            setCurrentStep("otp");
          } else {
            toast.error(response?.message || "Failed to initiate ABHA login.");
          }
        } catch (error) {
          console.error(error);
          toast.error("Something went wrong. Please try again.");
        } finally {
          setOtpLoading(false);
        }
      } else if (selectedOption === "aadhaar") {
        try {
          const aadhaarDigits = (inputValue || "").replace(/\D/g, "").slice(0, 12);

          setOtpLoading(true);

          const response = await abhaLogin({
            aadhaarNumber: aadhaarDigits,
            validConditionCount: "6",
          });


          if (response?.success) {
            setTxn_id(response?.txnId);
            setOtpTargetMessage("your Aadhaar-linked mobile number");
            setCurrentStep("otp");
          } else {
            toast.error(response?.message || "Failed to initiate Aadhaar login.");
          }
        } catch (error) {
          console.error(error);
          toast.error("Something went wrong. Please try again.");
        } finally {
          setOtpLoading(false);
        }
      } else if (selectedOption === "address") {
        // ABHA address – first fetch auth methods
        try {
          setOtpLoading(true);
          setAddressAuthLoading(true);
          if (existingPatient?.abhaDetails?.abhaAddress && existingPatient?.abhaDetails?.abhaAddress !== inputValue) {
            toast.error("ABHA Address does not match the existing patient.");
            return;
          }
          const response = await getAbhaAuthMethods({
            abhaAddress: inputValue,
          });
          if (response?.success) {
            setTxn_id(response?.txnId);
            const methods = response?.data || [];
            setAddressAuthMethods(methods);
            setSelectedAddressAuthMethod("");
            setCurrentStep("addressAuth");
          } else {
            toast.error(response?.message || "Failed to fetch auth methods.");
          }
        } catch (e) {
          console.error(e);
          toast.error("Something went wrong. Please try again.");
        } finally {
          setOtpLoading(false);
          setAddressAuthLoading(false);
        }
      }
    }
  };

  const handleBack = () => {
    if (currentStep === "abhaCard") {
      if (createFlow) {
        setCurrentStep("createAddress");
      } else {
        setCurrentStep(selectedOption === "phone" ? "linkedAccounts" : "otp");
      }
    } else if (currentStep === "addressAuth") {
      setCurrentStep("verification");
    } else if (currentStep === "createExistingAccounts") {
      setCurrentStep("createOtp");
    } else if (currentStep === "linkedAccounts") {
      setCurrentStep("verification");
      setSelectedLinkedProfileId(null);
    } else if (currentStep === "otp") {
      if (createFlow) {
        setCurrentStep("createAadhaar");
      } else {
        if (selectedOption === "phone") {
          setCurrentStep("linkedAccounts");
        } else if (selectedOption === "address") {
          setCurrentStep("addressAuth");
        } else {
          setCurrentStep("verification");
        }
        setOtp("");
      }
    } else if (currentStep === "verification") {
      setCurrentStep("selection");
      setInputValue("");
    } else if (currentStep === "createAddress") {
      setCurrentStep("createOtp");
      setAbhaAddress("");
      setSelectedSuggestedAddress("");
      setAddressValidationError("");
    } else if (currentStep === "createOtp") {
      setCurrentStep("createAadhaar");
      setOtp("");
    } else if (currentStep === "createAadhaar") {
      setCurrentStep("selection");
      setAadhaarNumber("");
      setCreateFlow(false);
    }
  };

  const handleCreateABHA = () => {
    setCreateFlow(true);
    setCurrentStep("createAadhaar");
  };

  const handleCreateAadhaarSubmit = async () => {
    try {
      const aadhaarDigits = (aadhaarNumber || "").replace(/\D/g, "").slice(0, 12);
      if (!aadhaarDigits || aadhaarDigits.length !== 12) return;

      const args = {
        aadhaarNumber: aadhaarDigits,
        validConditionCount: "6",
      };
      setCreateOtpLoading(true);
      const response = await createAbha(args);

      if (response?.success) {
        setTxn_id(response?.txnId);
        setCurrentStep("createOtp");
      } else {
        toast.error(response?.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCreateOtpLoading(false);
    }
  };

  const handleCreateOtpSubmit = async () => {
    try {
      if (otp.length === 6 && createMobileNumber.length === 10) {
        const args = {
          otp,
          txnId: txn_id,
          mobile: createMobileNumber,
        };
        setCreateVerifyOtpLoading(true);
        const response = await abhaCreateOtpVerify(args);

        if (response?.success) {
          toast.success(response?.message || "OTP verified successfully.");
          setToken(response?.tokens?.token);
          setTxn_id(response?.txnId);
          setAbhaProfile(response?.ABHAProfile);
          if (response?.isNew) {
            setCurrentStep("createAddress");
          } else {
            setCurrentStep("createExistingAccounts");
          }
        } else {
          toast.error(response?.message || "Failed to verify OTP.");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCreateVerifyOtpLoading(false);
    }
  };

  const handleSuggestedAddressSelect = (address) => {
    setSelectedSuggestedAddress(address);
    setAbhaAddress(address);
    setAddressValidationError("");
  };

  const validateABHAAddress = (address) => {
    if (address.length < 8) {
      return "Minimum Length - 8 Characters";
    }
    if (address.length > 18) {
      return "Maximum Length - 18 Characters";
    }

    const dotCount = (address.match(/\./g) || []).length;
    const underscoreCount = (address.match(/_/g) || []).length;

    if (dotCount > 1 || underscoreCount > 1) {
      return "Special character allowed - 1 dot (.) and/or 1 underscore (_)";
    }

    if (
      address.startsWith(".") ||
      address.startsWith("_") ||
      address.endsWith(".") ||
      address.endsWith("_")
    ) {
      return "Special characters can not be at the beginning or end";
    }

    if (!/^[a-zA-Z0-9._]+$/.test(address)) {
      return "Only alphanumeric characters, dots, and underscores are allowed";
    }

    return "";
  };

  const handleCreateAddressSubmit = async () => {
    if (!termsAccepted) return;

    const validationError = validateABHAAddress(abhaAddress);
    if (validationError) {
      setAddressValidationError(validationError);
      return;
    }

    try {
      setCreateAddressLoading(true);
      const args = {
        abhaAddress,
        txnId: txn_id,
      };
      const response = await createAbhaAddress(args);

      if (response?.success) {
        toast.success(response?.message || "ABHA Address created.");
        const profile = await getAbhaProfile({
          token,
          abhaaddress: false,
        });
        setAbhaProfile(profile);
        setCurrentStep("abhaCard");
      } else {
        toast.error(response?.message || "Failed to create ABHA Address.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCreateAddressLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    try {
      setVerifyOtpLoading(true);


      let args = {};
      let response;
      let authToken;

      if (selectedOption === "phone") {

        args = {
          txnId: txn_id,
          otp,
          mobile: true,
        };
      } else if (selectedOption === "aadhaar" || selectedOption === "abha") {

        args = {
          txnId: txn_id,
          otp,
          mobile: false,
        };
      } else if (selectedOption === "address") {

        args = {
          txnId: txn_id,
          otp,
          aadhaarnumber: selectedAddressAuthMethod === "AADHAAR_OTP",
        };
      }

      if (selectedOption === "address") {

        response = await abhaAddressOtpVerify(args);

        if (response?.success) {
          setTxn_id("");
          authToken = response?.tokens?.token;
        } else {
          toast.error(response?.message || "Failed to verify OTP.");
          return;
        }
      } else {
        response = await loginOtpVerify(args);
        if (response?.success) {
          setTxn_id(response?.txnId);
          authToken = response?.token;
        } else {
          toast.error(response?.message || "Failed to verify OTP.");
          return;
        }
      }

      if (response?.success && authToken) {
        const profile = await getAbhaProfile({
          token: authToken,
          abhaaddress: selectedOption === "address",
        });
        setAbhaProfile(profile);
        setToken(authToken);
        setCurrentStep("abhaCard");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtp("");
    // You can call resend API here if available
  };

  const handleLinkedProfileContinue = async (item) => {
    try {
      const response = await abhaLogin({
        index: `${item.index}`,
        txnId: txn_id,
      });

      if (response?.success) {
        setTxn_id(response?.txnId);
        setCurrentStep("otp");
      } else {
        toast.error(response?.message || "Failed to continue with selected profile.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleCopyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  const handleDownloadAbhaCard = () => {
    try {
      const url = abhaProfile?.abhaCardLink;
      if (!url) {
        toast.error("ABHA card link not available.");
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        toast.error("Invalid ABHA card link.");
        return;
      }
      const safeName =
        (abhaProfile?.name || "user").replace(/[<>:"/\\|?*]+/g, "_").trim() || "user";
      const link = document.createElement("a");
      link.href = url;
      link.download = `abha-card_${safeName}.png`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading ABHA card:", error);
      toast.error("Failed to download ABHA card. Please try again later.");
    }
  };

  const handleTermsModalOpen = () => {
    setShowTermsModal(true);
  };

  const handleTermsModalClose = () => {
    setShowTermsModal(false);
  };

  const handleCheckboxChange = (term) => {
    setCheckedTerms((prev) => ({
      ...prev,
      [term]: !prev[term],
    }));
  };

  const handleTermsAccept = () => {
    const allChecked = {
      term1: true,
      term2: true,
      term3: true,
    };
    setCheckedTerms(allChecked);
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const allTermsChecked = Object.values(checkedTerms).every((checked) => checked);

  const renderTermsModal = () => {
    if (!showTermsModal) return null;

    return (
      <Stack spacing={2}>
        <Typography sx={mutedTextSx}>I hereby declare that:</Typography>
        {[
          {
            id: "term1",
            title: "Data Privacy and Security",
            copy:
              "I understand that my personal health data will be stored securely and will only be accessed by authorized healthcare providers with my explicit consent. This includes protection against unauthorized access, data breaches, and ensuring compliance with applicable data protection regulations.",
          },
          {
            id: "term2",
            title: "Consent for Data Sharing",
            copy:
              "I consent to sharing my health data with healthcare providers, hospitals, and other authorized medical institutions for the purpose of providing better healthcare services. This includes emergency situations where immediate access to my health information may be critical for my safety and well-being.",
          },
          {
            id: "term3",
            title: "Account Responsibility",
            copy:
              "I am responsible for maintaining the confidentiality of my ABHA credentials and will notify immediately if there is any unauthorized access to my account. I understand that I must keep my login credentials secure and not share them with others.",
          },
        ].map((term) => (
          <Box key={term.id} sx={{ ...cardSx, p: 1.5, backgroundColor: "var(--app-surface-2)" }}>
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                m: 0,
                color: "var(--app-text)",
                "& .MuiFormControlLabel-label": { fontSize: 14, lineHeight: 1.55 },
              }}
              control={
                <Checkbox
                  checked={checkedTerms[term.id]}
                  onChange={() => handleCheckboxChange(term.id)}
                  sx={{
                    color: "var(--app-text-muted)",
                    pt: 0,
                    "&.Mui-checked": { color: "#068F06" },
                  }}
                />
              }
              label={
                <>
                  <Typography component="span" sx={{ fontWeight: 800 }}>
                    {term.title}:
                  </Typography>{" "}
                  {term.copy}
                </>
              }
            />
          </Box>
        ))}
        <Button
          type="button"
          fullWidth
          onClick={handleTermsAccept}
          disabled={!allTermsChecked}
          sx={primaryButtonSx}
        >
          I Agree
        </Button>
      </Stack>
    );
  };

  const renderPhoneNumberUI = () => (
    <Stack spacing={2}>
      <Typography sx={mutedTextSx}>
        We will send you a one-time password to the phone number linked to your Aadhaar.
      </Typography>
      <Box>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
          Phone Number
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box sx={{ width: 120, flex: "0 0 auto" }}>
            <CountryCodePicker onChange={setCountryCode} />
          </Box>
          <TextField
            fullWidth
            type="tel"
            placeholder="9876543210"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            sx={fieldSx}
          />
        </Box>
      </Box>
    </Stack>
  );

  const renderAadhaarUI = () => (
    <Stack spacing={2}>
      <Typography sx={mutedTextSx}>We will send you a one-time password for Validation.</Typography>
      <Box>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
          Aadhaar Number*
        </Typography>
        <TextField
          fullWidth
          placeholder="9972-9729-2323"
          value={formatAadhaarNumber(inputValue)}
          onChange={(e) => setInputValue(e.target.value)}
          sx={fieldSx}
        />
      </Box>
    </Stack>
  );

  const renderABHAUI = () => (
    <Stack spacing={2.5}>
      <Typography sx={mutedTextSx}>We will send you a one-time password for Validation.</Typography>
      <Box>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
          ABHA Number*
        </Typography>
        <TextField
          fullWidth
          placeholder="91-6626-4148-0808"
          value={formatAbhaNumber(inputValue)}
          onChange={(e) => setInputValue(e.target.value)}
          sx={fieldSx}
        />
      </Box>
      <Box>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1.5 }}>
          Validate Using
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
          <AuthMethodCard
            selected={abhaValidateMethod === "aadhaar"}
            label="Aadhaar OTP"
            image={aadhaar}
            onClick={() => setAbhaValidateMethod("aadhaar")}
          />
          <AuthMethodCard
            selected={abhaValidateMethod === "mobile"}
            label="Mobile OTP"
            image={mobile}
            onClick={() => setAbhaValidateMethod("mobile")}
          />
        </Box>
      </Box>
    </Stack>
  );

  const renderABHAAddressUI = () => (
    <Stack spacing={2}>
      <Typography sx={mutedTextSx}>We will send you a one-time password for Validation.</Typography>
      <Box>
        <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
          ABHA Address*
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter ABHA Address"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          sx={fieldSx}
        />
      </Box>
    </Stack>
  );

  // Address Auth Method selection step
  if (currentStep === "addressAuth") {
    return (
      <ModalShell title="Choose Authentication Method" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Typography sx={mutedTextSx}>
            Select a method to authenticate for{" "}
            <Typography component="span" sx={{ color: "var(--app-text)", fontWeight: 800 }}>
              {inputValue}
            </Typography>
            .
          </Typography>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              ABHA Address
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter ABHA Address"
              value={inputValue}
              disabled
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1.5 }}>
              Validate Using
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
              {(addressAuthMethods || []).map((m, i) => (
                <AuthMethodCard
                  key={`${m}-${i}`}
                  selected={selectedAddressAuthMethod === m}
                  label={String(m).replace(/_/g, " ").toUpperCase()}
                  image={m === "AADHAAR_OTP" ? aadhaar : mobile}
                  onClick={() => setSelectedAddressAuthMethod(m)}
                />
              ))}
            </Box>
          </Box>
          <Typography sx={{ color: "var(--app-text)", fontSize: 14 }}>
            Do not have an ABHA number?{" "}
            <Button sx={ghostButtonSx} onClick={handleCreateABHA}>
              Create now
            </Button>
          </Typography>
          <LoadingButton
            fullWidth
            onClick={async () => {
              if (!selectedAddressAuthMethod) return;
              try {
                setAddressAuthLoading(true);
                const response = await abhaLoginUsingAddress({
                  abhaaddress: inputValue,
                  aadhaarnumber: selectedAddressAuthMethod === "AADHAAR_OTP",
                });
                if (response?.success) {
                  setTxn_id(response?.txnId);
                  setOtpTargetMessage("your registered mobile number");
                  setCurrentStep("otp");
                } else {
                  toast.error(response?.message || "Failed to get OTP.");
                }
              } catch (e) {
                console.error(e);
                toast.error("Something went wrong. Please try again.");
              } finally {
                setAddressAuthLoading(false);
              }
            }}
            disabled={!selectedAddressAuthMethod || addressAuthLoading}
            loading={addressAuthLoading}
            sx={primaryButtonSx}
          >
            Get OTP
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  const handleGetAbhaProfile = async () => {
    try {
      const response = await getAbhaProfile({
        token,
        abhaaddress: false,
      });
      setAbhaProfile(response);
      setCurrentStep("abhaCard");
    } catch (error) {
      console.error(error);
      toast.error("Unable to fetch ABHA profile.");
    }
  };

  const renderSelectedOptionUI = () => {
    switch (selectedOption) {
      case "phone":
        return renderPhoneNumberUI();
      case "aadhaar":
        return renderAadhaarUI();
      case "abha":
        return renderABHAUI();
      case "address":
        return renderABHAAddressUI();
      default:
        return renderPhoneNumberUI();
    }
  };

  // Step 1: Option Selection
  if (currentStep === "selection") {
    return (
      <ModalShell title="Login to ABHA" onClose={onClose}>
        <Stack spacing={1.5}>
          <OptionCard
            selected={selectedOption === "phone"}
            label="Phone Number"
            icon="mdi:phone-outline"
            onClick={() => handleOptionChange("phone")}
          />
          <OptionCard
            selected={selectedOption === "aadhaar"}
            label="Aadhaar Number"
            icon="mdi:card-account-details-outline"
            onClick={() => handleOptionChange("aadhaar")}
          />
          <OptionCard
            selected={selectedOption === "abha"}
            label="ABHA Number"
            icon="mdi:identifier"
            onClick={() => handleOptionChange("abha")}
          />
          <OptionCard
            selected={selectedOption === "address"}
            label="ABHA Address"
            icon="mdi:at"
            onClick={() => handleOptionChange("address")}
          />
          <Typography sx={{ color: "var(--app-text)", fontSize: 14, pt: 1 }}>
            Do not have an ABHA number?{" "}
            <Button type="button" sx={ghostButtonSx} onClick={handleCreateABHA}>
              Create now
            </Button>
          </Typography>
          <Button fullWidth onClick={handleContinue} sx={{ ...primaryButtonSx, mt: 1 }}>
            Continue
          </Button>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "selection") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">Login to ABHA</h5>
          <Icon
  icon="mdi:close"
  width="24"
  height="24"
  onClick={onClose}
  style={{
    cursor: "pointer",
    color: "var(--app-text)",
  }}
/>
        </div>

        <div className="modal-body pt-10">
          <div className="mb-4">
            {/* Phone Number Option */}
            <div
              className={`input-option p-3 mb-3 rounded-3 border ${selectedOption === "phone" ? "border-success" : "border-light"
                }`}
              style={{
                backgroundColor: "var(--app-surface-2)",
                cursor: "pointer",
              }}
              onClick={() => handleOptionChange("phone")}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
  <span
    
    style={{ color: "var(--app-text)" }}
  >
    Phone Number
  </span>
</div>
                <div
                  className={`selection-indicator ${selectedOption === "phone" ? "selected" : "unselected"
                    }`}
                >
                  {selectedOption === "phone" ? (
                    <div
                      className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <i className="text-white" style={{ fontSize: "12px" }}>
                        ✓
                      </i>
                    </div>
                  ) : (
                    <div
                      className="bg-light rounded-circle"
                      style={{ width: "20px", height: "20px" }}
                    ></div>
                  )}
                </div>
              </div>
            </div>

            {/* Aadhaar Number Option */}
            <div
              className={`input-option p-3 mb-3 rounded-3 border ${selectedOption === "aadhaar" ? "border-success" : "border-light"
                }`}
              style={{
                backgroundColor: "var(--app-surface-2)",
                cursor: "pointer",
              }}
              onClick={() => handleOptionChange("aadhaar")}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <span
    
    style={{ color: "var(--app-text)" }}
  >Aadhaar Number</span>
                </div>
                <div
                  className={`selection-indicator ${selectedOption === "aadhaar" ? "selected" : "unselected"
                    }`}
                >
                  {selectedOption === "aadhaar" ? (
                    <div
                      className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <i className="text-white" style={{ fontSize: "12px" }}>
                        ✓
                      </i>
                    </div>
                  ) : (
                    <div
                      className="bg-light rounded-circle"
                      style={{ width: "20px", height: "20px" }}
                    ></div>
                  )}
                </div>
              </div>
            </div>

            {/* ABHA Number Option */}
            <div
              className={`input-option p-3 mb-3 rounded-3 border ${selectedOption === "abha" ? "border-success" : "border-light"
                }`}
              style={{
                backgroundColor: "var(--app-surface-2)",
                cursor: "pointer",
              }}
              onClick={() => handleOptionChange("abha")}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <span
    
    style={{ color: "var(--app-text)" }}
  >ABHA Number</span>
                </div>
                <div
                  className={`selection-indicator ${selectedOption === "abha" ? "selected" : "unselected"
                    }`}
                >
                  {selectedOption === "abha" ? (
                    <div
                      className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <i className="text-white" style={{ fontSize: "12px" }}>
                        ✓
                      </i>
                    </div>
                  ) : (
                    <div
                      className="bg-light rounded-circle"
                      style={{ width: "20px", height: "20px" }}
                    ></div>
                  )}
                </div>
              </div>
            </div>

            {/* ABHA Address Option */}
            <div
              className={`input-option p-3 mb-3 rounded-3 border ${selectedOption === "address" ? "border-success" : "border-light"
                }`}
              style={{
                backgroundColor: "var(--app-surface-2)",
                cursor: "pointer",
              }}
              onClick={() => handleOptionChange("address")}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <span
    
    style={{ color: "var(--app-text)" }}
  >ABHA Address</span>
                </div>
                <div
                  className={`selection-indicator ${selectedOption === "address" ? "selected" : "unselected"
                    }`}
                >
                  {selectedOption === "address" ? (
                    <div
                      className="bg-success rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px" }}
                    >
                      <i className="text-white" style={{ fontSize: "12px" }}>
                        ✓
                      </i>
                    </div>
                  ) : (
                    <div
                      className="bg-light rounded-circle"
                      style={{ width: "20px", height: "20px" }}
                    ></div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Create ABHA Link */}
          <div className="text-left mb-4">
            <span
    
    style={{ color: "var(--app-text)" }}
  >Do not have an ABHA number? </span>
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 border-0"
              style={{ color: "#5751E9" }}
              onClick={handleCreateABHA}
            >
              Create now
            </button>
          </div>

          {/* Continue Button */}
          <Button className="btn btn-added-primary w-100" onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  // Step 1.5: Create ABHA - Aadhaar Input
  if (currentStep === "createAadhaar") {
    if (showTermsModal) {
      return (
        <ModalShell title="Terms and Conditions" onClose={onClose} action={
          <IconButton
            size="small"
            onClick={handleTermsModalClose}
            aria-label="Close terms"
            sx={{ color: "var(--app-text-muted)" }}
          >
            <Icon icon="mdi:close" width={22} height={22} />
          </IconButton>
        }>
          {renderTermsModal()}
        </ModalShell>
      );
    }

    return (
      <ModalShell title="Create ABHA Account" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={mutedTextSx}>
              Please enter your Aadhaar number to create your ABHA account.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              Aadhaar Number*
            </Typography>
            <TextField
              fullWidth
              placeholder="9972-9729-2323"
              value={formatAadhaarNumber(aadhaarNumber)}
              onChange={(e) => setAadhaarNumber(e.target.value)}
              inputProps={{ maxLength: 14 }}
              sx={fieldSx}
            />
          </Box>
          <FormControlLabel
            sx={{
              m: 0,
              alignItems: "flex-start",
              color: "var(--app-text)",
              "& .MuiFormControlLabel-label": { fontSize: 14, lineHeight: 1.5 },
            }}
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                sx={{ pt: 0, "&.Mui-checked": { color: "#068F06" } }}
              />
            }
            label={
              <>
                I have read all the{" "}
                <Button type="button" sx={ghostButtonSx} onClick={handleTermsModalOpen}>
                  Terms & Conditions
                </Button>
              </>
            }
          />
          <LoadingButton
            fullWidth
            onClick={handleCreateAadhaarSubmit}
            disabled={
              aadhaarNumber.replace(/\D/g, "").length !== 12 ||
              createOtpLoading ||
              !termsAccepted
            }
            loading={createOtpLoading}
            sx={primaryButtonSx}
          >
            Continue
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "createAadhaar") {
    return (
      <div className="">
        {showTermsModal ? (
          <>
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">Terms and Conditions </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleTermsModalClose}
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body pt-0">{renderTermsModal()}</div>
          </>
        ) : (
          <>
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">Create ABHA Account</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body pt-0">
              <div className="mb-4">
                <p className="text-muted2 mb-3">
                  Please enter your Aadhaar number to create your ABHA account.
                </p>
                <label className="form-label fw-bold">Aadhaar Number*</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="9972-9729-2323"
                  value={formatAadhaarNumber(aadhaarNumber)}
                  onChange={(e) => setAadhaarNumber(e.target.value)}
                  style={{ height: "48px", borderRadius: "8px" }}
                  maxLength={14}
                />
              </div>

              {/* Terms & Conditions */}
              <div className="mb-4">
                <div className="form-check">
                  <input
                    className="form-check-input terms-checkbox"
                    type="checkbox"
                    id="termsCheckAadhaar"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="termsCheckAadhaar"
                  >
                    I have read all the{" "}
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none p-0 border-0"
                      style={{ color: "#5751E9" }}
                      onClick={handleTermsModalOpen}
                    >
                      Terms & Conditions
                    </button>
                  </label>
                </div>
              </div>

              {/* Continue Button */}
              <button
                className="btn btn-added-primary w-100"
                onClick={handleCreateAadhaarSubmit}
                disabled={
                  aadhaarNumber.replace(/\D/g, "").length !== 12 ||
                  createOtpLoading ||
                  !termsAccepted
                }
              >
                {createOtpLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Sending OTP
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Step 2.5: Create ABHA - OTP Verification
  if (currentStep === "createOtp") {
    return (
      <ModalShell title="Verify OTP" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Typography sx={mutedTextSx}>
            Please enter the OTP sent to your registered mobile number linked with Aadhaar.
          </Typography>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              OTP
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <OtpInput
                value={otp}
                onChange={setOtp}
                length={6}
                disabled={createVerifyOtpLoading}
                autoFocus
              />
            </Box>
          </Box>
          <Box sx={{ color: "var(--app-text-muted)", fontSize: 14 }}>
            <Timer
              seconds={59}
              minutes={0}
              resend={handleResendOTP}
              text={"Resend OTP in"}
              buttonColor={"#5751E9"}
              background={"transparent"}
              ButtonText={"Didn't get the code? Resend OTP"}
            />
          </Box>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              Contact Number
            </Typography>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <Box sx={{ width: 120, flex: "0 0 auto" }}>
                <CountryCodePicker disabled />
              </Box>
              <TextField
                fullWidth
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={createMobileNumber}
                onChange={(e) =>
                  setCreateMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                inputProps={{ maxLength: 10 }}
                sx={fieldSx}
              />
            </Box>
          </Box>
          <Alert
            severity="warning"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#FFECD6",
              color: "var(--app-text)",
              "& .MuiAlert-icon": { color: "#C77800" },
            }}
          >
            The number you enter will be linked to ABHA.
          </Alert>
          <LoadingButton
            fullWidth
            onClick={handleCreateOtpSubmit}
            loading={createVerifyOtpLoading}
            disabled={
              otp.length !== 6 || createMobileNumber.length !== 10 || createVerifyOtpLoading
            }
            sx={primaryButtonSx}
          >
            Verify OTP
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "createOtp") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">Verify OTP</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body pt-0">
          <div className="mb-4">
            <p className="text-muted2 mb-3">
              Please enter the OTP sent to your registered mobile number linked with
              Aadhaar.
            </p>
          </div>

          <div className="mb-4">
            <label className="form-label fw-bold">OTP</label>
            <div className="d-flex justify-content-center">
              <OtpInput
                  value={otp}
                  onChange={setOtp}
                  length={6}
                  disabled={createVerifyOtpLoading}
                  autoFocus
                />
             
            </div>
          </div>

          <div className="mb-4">
            <Timer
              seconds={59}
              minutes={0}
              resend={handleResendOTP}
              text={"Resend OTP in"}
              buttonColor={"#5751E9"}
              background={"#fff"}
              ButtonText={"Didn't get the code? Resend OTP"}
            />
          </div>

          <div className="mb-4">
            <label className="form-label fw-bold">Contact Number</label>
            <div className="row">
              <div className="col-md-3">
                <CountryCodePicker disabled={true} />
              </div>

              <div className="col-md-9">
                <input
                  type="tel"
                  className="form-control"
                  placeholder="Enter 10-digit mobile number"
                  value={createMobileNumber}
                  onChange={(e) =>
                    setCreateMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  style={{ height: "48px", borderRadius: "8px" }}
                  maxLength={10}
                />
              </div>
            </div>
          </div>
          <div className="alert alert-warning">
            <p className="text-muted2">The number you enter will be linked to ABHA.</p>
          </div>

          <LoadingButton
            className="btn btn-added-primary w-100"
            onClick={handleCreateOtpSubmit}
            loading={createVerifyOtpLoading}
            disabled={
              otp.length !== 6 || createMobileNumber.length !== 10 || createVerifyOtpLoading
            }
          >
            {createVerifyOtpLoading ? "Verifying OTP" : "Verify OTP"}
          </LoadingButton>
        </div>
      </div>
    );
  }

  // Step 3.5: Create ABHA - ABHA Address Creation
  if (currentStep === "createAddress") {
    const minLengthOk = abhaAddress.length >= 8;
    const maxLengthOk = abhaAddress.length <= 18 && abhaAddress.length > 0;
    const dotCount = (abhaAddress.match(/\./g) || []).length;
    const underscoreCount = (abhaAddress.match(/_/g) || []).length;
    const specialCountOk =
      dotCount <= 1 && underscoreCount <= 1 && abhaAddress.length > 0;
    const specialPositionOk =
      abhaAddress.length > 0 &&
      !abhaAddress.startsWith(".") &&
      !abhaAddress.startsWith("_") &&
      !abhaAddress.endsWith(".") &&
      !abhaAddress.endsWith("_");
    const allowedCharsOk = /^[a-zA-Z0-9._]+$/.test(abhaAddress) && abhaAddress.length > 0;
    const allRulesOk =
      minLengthOk && maxLengthOk && specialCountOk && specialPositionOk && allowedCharsOk;

    if (showTermsModal) {
      return (
        <ModalShell title="Terms and Conditions" onClose={onClose} action={
          <IconButton
            size="small"
            onClick={handleTermsModalClose}
            aria-label="Close terms"
            sx={{ color: "var(--app-text-muted)" }}
          >
            <Icon icon="mdi:close" width={22} height={22} />
          </IconButton>
        }>
          {renderTermsModal()}
        </ModalShell>
      );
    }

    const ruleItems = [
      { ok: minLengthOk, text: "Minimum length - 8 characters" },
      { ok: maxLengthOk, text: "Maximum length - 18 characters" },
      { ok: specialCountOk, text: "Allow at most 1 dot (.) and/or 1 underscore (_)" },
      { ok: specialPositionOk, text: "Dot/underscore can not be at the start or end" },
      { ok: allowedCharsOk, text: "Only letters, numbers, dot (.) and underscore (_)" },
    ];

    return (
      <ModalShell title="Create ABHA Address" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Typography sx={mutedTextSx}>
            Create ABHA address either manually or through suggested options given below.
          </Typography>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              ABHA Address
            </Typography>
            <TextField
              fullWidth
              placeholder="Eg : XXXXX000000"
              value={abhaAddress}
              onChange={(e) => {
                setAbhaAddress(e.target.value);
                setAddressValidationError("");
              }}
              error={Boolean(addressValidationError)}
              helperText={addressValidationError}
              sx={fieldSx}
            />
          </Box>
          <Divider sx={{ borderColor: "var(--app-border)" }}>
            <Chip
              label="Or"
              size="small"
              sx={{
                backgroundColor: "var(--app-surface)",
                color: "var(--app-text-muted)",
                border: "1px solid var(--app-border)",
              }}
            />
          </Divider>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1.5 }}>
              Suggested ABHA Address
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {["Johndoe_828", "Johndoe_828", "Johndoe_828", "Johndoe_828"].map(
                (address, index) => (
                  <Chip
                    key={`${address}-${index}`}
                    label={address}
                    clickable
                    onClick={() => handleSuggestedAddressSelect(address)}
                    sx={{
                      height: 36,
                      px: 0.5,
                      fontWeight: 700,
                      color:
                        selectedSuggestedAddress === address ? "#fff" : "var(--app-text)",
                      backgroundColor:
                        selectedSuggestedAddress === address ? "#5751E9" : "var(--app-surface-2)",
                      border: "1px solid",
                      borderColor:
                        selectedSuggestedAddress === address ? "#5751E9" : "var(--app-border)",
                      "&:hover": {
                        backgroundColor:
                          selectedSuggestedAddress === address ? "#4a43d6" : "var(--app-hover)",
                      },
                    }}
                  />
                )
              )}
            </Stack>
          </Box>
          <FormControlLabel
            sx={{
              m: 0,
              alignItems: "flex-start",
              color: "var(--app-text)",
              "& .MuiFormControlLabel-label": { fontSize: 14, lineHeight: 1.5 },
            }}
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                sx={{ pt: 0, "&.Mui-checked": { color: "#068F06" } }}
              />
            }
            label={
              <>
                I have read all the{" "}
                <Button type="button" sx={ghostButtonSx} onClick={handleTermsModalOpen}>
                  Terms & Conditions
                </Button>
              </>
            }
          />
          <Box sx={{ ...cardSx, p: 2, backgroundColor: "var(--app-surface-2)" }}>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              ABHA address rules
            </Typography>
            <Stack spacing={0.75}>
              {ruleItems.map((rule) => (
                <Box key={rule.text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon
                    icon={rule.ok ? "mdi:check-circle" : "mdi:close-circle"}
                    width={17}
                    height={17}
                    color={rule.ok ? "#068F06" : "#E72020"}
                  />
                  <Typography
                    sx={{
                      color: rule.ok ? "#068F06" : "#E72020",
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    {rule.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <LoadingButton
            fullWidth
            onClick={handleCreateAddressSubmit}
            disabled={!termsAccepted || !allRulesOk || createAddressLoading}
            loading={createAddressLoading}
            sx={primaryButtonSx}
          >
            Continue
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "createAddress") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">Create ABHA Address</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body pt-0">
          <div className="mb-4">
            <p className="text-muted2 mb-3">
              Create ABHA address either manually or through suggested options
              given below.
            </p>
          </div>

          <div className="mb-4">
            <label className="form-label fw-bold">ABHA Address</label>
            <input
              type="text"
              className="form-control"
              placeholder="Eg : XXXXX000000"
              value={abhaAddress}
              onChange={(e) => {
                setAbhaAddress(e.target.value);
                setAddressValidationError("");
              }}
              style={{ height: "48px", borderRadius: "8px" }}
            />
            {addressValidationError && (
              <div className="text-danger mt-2 small">
                {addressValidationError}
              </div>
            )}
          </div>

          <div className="text-center mb-4">
            <div className="position-relative">
              <hr />
              <span className="bg-white px-3 position-absolute top-50 start-50 translate-middle">
                Or
              </span>
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label fw-bold">Suggested ABHA Address</label>
            <div className="d-flex gap-2 flex-wrap">
              {["Johndoe_828", "Johndoe_828", "Johndoe_828", "Johndoe_828"].map(
                (address, index) => (
                  <button
                    key={index}
                    className={`btn rounded-pill ${selectedSuggestedAddress === address
                      ? "btn-added-primary"
                      : "btn-outline-secondary"
                      }`}
                    onClick={() => handleSuggestedAddressSelect(address)}
                  >
                    {address}
                  </button>
                )
              )}
            </div>
          </div>

          <div className="mb-4">
            <div className="form-check">
              <input
                className="form-check-input terms-checkbox"
                type="checkbox"
                id="termsCheck"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="termsCheck">
                I have read all the{" "}
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 border-0"
                  style={{ color: "#5751E9" }}
                  onClick={handleTermsModalOpen}
                >
                  Terms & Conditions
                </button>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold">ABHA address rules</h6>
            <ul className="small list-unstyled mb-0">
              <li className={minLengthOk ? "text-success" : "text-danger"}>
                {minLengthOk ? "✓" : "✗"} Minimum length - 8 characters
              </li>
              <li className={maxLengthOk ? "text-success" : "text-danger"}>
                {maxLengthOk ? "✓" : "✗"} Maximum length - 18 characters
              </li>
              <li className={specialCountOk ? "text-success" : "text-danger"}>
                {specialCountOk ? "✓" : "✗"} Allow at most 1 dot (.) and/or 1 underscore (_)
              </li>
              <li className={specialPositionOk ? "text-success" : "text-danger"}>
                {specialPositionOk ? "✓" : "✗"} Dot/underscore can not be at the start or end
              </li>
              <li className={allowedCharsOk ? "text-success" : "text-danger"}>
                {allowedCharsOk ? "✓" : "✗"} Only letters, numbers, dot (.) and underscore (_)
              </li>
            </ul>
          </div>

          <LoadingButton
            className="btn btn-added-primary w-100"
            onClick={handleCreateAddressSubmit}
            disabled={!termsAccepted || !allRulesOk || createAddressLoading}
            loading={createAddressLoading}
          >
            Continue
          </LoadingButton>
        </div>

        {renderTermsModal()}
      </div>
    );
  }

  // Step 3 (Create flow when existing ABHA found): Existing Accounts + CTA
  if (currentStep === "createExistingAccounts") {
    return (
      <ModalShell title="ABHA Account Found" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Alert
            severity="info"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#F2F2FF",
              color: "#5751E9",
              "& .MuiAlert-icon": { color: "#5751E9" },
            }}
          >
            You already have an ABHA account linked to this Aadhaar/mobile.
          </Alert>
          <Box
            onClick={handleGetAbhaProfile}
            sx={{
              ...cardSx,
              p: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              backgroundColor: "var(--app-surface-2)",
              "&:hover": { borderColor: "#5751E9", backgroundColor: "var(--app-hover)" },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "var(--app-text)", fontWeight: 800 }}>
                {abhaProfile?.userData?.firstName} {abhaProfile?.userData?.middleName}{" "}
                {abhaProfile?.userData?.lastName}
              </Typography>
              <Typography sx={mutedTextSx}>{abhaProfile?.ABHANumber}</Typography>
              <Typography sx={mutedTextSx}>
                Gender: {abhaProfile?.userData?.gender === "M" ? "Male" : "Female"}
              </Typography>
            </Box>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentStep("abhaCard");
              }}
              sx={{ ...primaryButtonSx, width: 120, flex: "0 0 auto" }}
            >
              Continue
            </Button>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={mutedTextSx}>Want to create a new ABHA Address?</Typography>
            <Button type="button" sx={ghostButtonSx} onClick={() => setCurrentStep("createAddress")}>
              Create New ABHA Address
            </Button>
          </Box>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "createExistingAccounts") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">ABHA Account Found</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body pt-0">
          <div className="alert alert-info mb-3">
            You already have an ABHA account linked to this Aadhaar/mobile.
          </div>

          <div className="mb-3">
            <div
              onClick={handleGetAbhaProfile}
              className="border rounded-3 p-3 d-flex align-items-center justify-content-between"
            >
              <div>
                <div className="fw-bold">
                  {abhaProfile?.userData?.firstName} {abhaProfile?.userData?.middleName}{" "}
                  {abhaProfile?.userData?.lastName}
                </div>
                <div className="text-muted2 small">{abhaProfile?.ABHANumber}</div>
                <div className="text-muted2 small">
                  Gender: {abhaProfile?.userData?.gender === "M" ? "Male" : "Female"}
                </div>
              </div>
              <button
                className="btn btn-added-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentStep("abhaCard");
                }}
              >
                Continue
              </button>
            </div>
          </div>

          <div className="text-center mt-4">
            <div className="text-muted2">
              Want to create a new ABHA Address?
            </div>
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 border-0"
              style={{ color: "#5751E9" }}
              onClick={() => setCurrentStep("createAddress")}
            >
              Create New ABHA Address
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Verification UI
  if (currentStep === "verification") {
    return (
      <ModalShell title="Login to ABHA" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          {renderSelectedOptionUI()}
          <Typography sx={{ color: "var(--app-text)", fontSize: 14 }}>
            Do not have an ABHA number?{" "}
            <Button type="button" sx={ghostButtonSx} onClick={handleCreateABHA}>
              Create now
            </Button>
          </Typography>
          <LoadingButton
            fullWidth
            onClick={handleContinue}
            disabled={!inputValue.trim() || otpLoading}
            loading={otpLoading}
            sx={primaryButtonSx}
          >
            Get OTP
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "verification") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">Login to ABHA</h5>
          <button
            type="button"
            onClick={onClose}
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body pt-0">
          {renderSelectedOptionUI()}

          <div className="text-left mb-4">
            <span
    
    style={{ color: "var(--app-text)" }}
  >Do not have an ABHA number? </span>
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 border-0"
              style={{ color: "#5751E9" }}
              onClick={handleCreateABHA}
            >
              Create now
            </button>
          </div>

          <LoadingButton
            className="btn btn-added-primary w-100"
            onClick={handleContinue}
            disabled={!inputValue.trim() || otpLoading}
            loading={otpLoading}
          >
            Get OTP
          </LoadingButton>
        </div>
      </div>
    );
  }

  // Step 3: OTP Verification (login)
  // Step 3: OTP Verification (login)
  if (currentStep === "otp") {
    const otpDestination =
      selectedOption === "phone"
        ? maskedNumber
        : otpTargetMessage || "your registered mobile number";

    return (
      <ModalShell title="Enter OTP to Verify Number" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={mutedTextSx}>
              Please enter the OTP sent {selectedOption === "phone" ? "at" : "to"}{" "}
              <Typography component="span" sx={{ color: "var(--app-text)", fontWeight: 800 }}>
                {otpDestination}
              </Typography>
            </Typography>
            <IconButton
              size="small"
              onClick={() => setCurrentStep("verification")}
              sx={{ color: "#5751E9" }}
              aria-label="Edit verification detail"
            >
              <Icon icon="mdi:pencil-outline" width={18} height={18} />
            </IconButton>
          </Box>
          <Box>
            <Typography sx={{ color: "var(--app-text)", fontWeight: 800, mb: 1 }}>
              OTP
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <OtpInput
                value={otp}
                onChange={setOtp}
                length={6}
                disabled={verifyOtpLoading}
                autoFocus
              />
            </Box>
          </Box>
          <Box sx={{ color: "var(--app-text-muted)", fontSize: 14 }}>
            <Timer
              seconds={59}
              minutes={0}
              resend={handleResendOTP}
              text={"Resend OTP in"}
              buttonColor={"transparent"}
              background={"transparent"}
              ButtonText={""}
            />
          </Box>
          <LoadingButton
            fullWidth
            onClick={handleVerifyOTP}
            disabled={otp.length !== 6 || verifyOtpLoading}
            loading={verifyOtpLoading}
            sx={primaryButtonSx}
          >
            Verify OTP
          </LoadingButton>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "otp") {
    return (
      <Box sx={{ p: 2, borderRadius: "8px" }}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          {/* back arrow */}
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{ mr: 1 }}
            aria-label="Back"
          >
            <Icon icon="mdi:chevron-left" width={24} height={24} />
          </IconButton>

          <Typography
            variant="h6"
            sx={{ fontWeight: 700, flexGrow: 1 }}
            noWrap
          >
            Enter OTP to Verify Number
          </Typography>

          {/* close */}
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon icon="mdi:close" width={22} height={22} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box pt={0} pl={1}>
          {/* subtitle */}
          <Box mb={4}>
            {selectedOption === "phone" ? (
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Please enter the OTP sent at{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {maskedNumber}
                  </Typography>
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setCurrentStep("verification")}
                  sx={{ ml: 1 }}
                >
                  <Icon icon="mdi:pencil-outline" width={18} height={18} />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Please enter the OTP sent to{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {otpTargetMessage || "your registered mobile number"}
                  </Typography>
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setCurrentStep("verification")}
                  sx={{ ml: 1 }}
                >
                  <Icon icon="mdi:pencil-outline" width={18} height={18} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* OTP boxes */}
          <Box mb={4}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              OTP
            </Typography>
            <Box display="flex" justifyContent="center">
              <OtpInput
                  value={otp}
                  onChange={setOtp}
                  length={6}
                  disabled={verifyOtpLoading}
                  autoFocus
                />
            </Box>
          </Box>

          {/* timer text */}
          <Box mb={4}>
            <Typography variant="body2" color="text.secondary">
              <Timer
                seconds={59}
                minutes={0}
                resend={handleResendOTP}
                text={"Resend OTP in"}
                buttonColor={"transparent"}
                background={"transparent"}
                ButtonText={""}
              />
            </Typography>
          </Box>

          {/* verify button */}
          <LoadingButton
            fullWidth
            onClick={handleVerifyOTP}
            disabled={otp.length !== 6 || verifyOtpLoading}
            loading={verifyOtpLoading}
            sx={{
              borderRadius: "8px",
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor:
                otp.length === 6 && !verifyOtpLoading ? "#5751E9" : "var(--app-border)",
              color:
                otp.length === 6 && !verifyOtpLoading ? "#FFFFFF" : "var(--app-text-muted)",
              "&:hover": {
                backgroundColor:
                  otp.length === 6 && !verifyOtpLoading ? "#4a43d6" : "var(--app-border)",
              },
            }}
          >
            Verify OTP
          </LoadingButton>
        </Box>
      </Box>

    );
  }


  // Step 2 (Phone only): Linked ABHA Accounts
  if (currentStep === "linkedAccounts" && selectedOption === "phone") {
    return (
      <ModalShell title="Select Linked ABHA Profile" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2}>
          <Typography sx={mutedTextSx}>
            Choose a profile linked with{" "}
            <Typography component="span" sx={{ color: "var(--app-text)", fontWeight: 800 }}>
              {maskedNumber}
            </Typography>
          </Typography>
          <Stack spacing={1.5}>
            {linkedProfiles?.map((p, i) => (
              <Box
                key={i}
                onClick={() => handleLinkedProfileContinue(p)}
                sx={{
                  ...cardSx,
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  backgroundColor: "var(--app-surface-2)",
                  "&:hover": {
                    borderColor: "#5751E9",
                    backgroundColor: "var(--app-hover)",
                  },
                }}
              >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "var(--app-text)", fontSize: 14 }}>
                    <Typography component="span" sx={{ fontWeight: 800 }}>
                      Name:{" "}
                    </Typography>
                    {p.name}
                  </Typography>
                  <Typography sx={{ color: "var(--app-text)", fontSize: 14 }}>
                    <Typography component="span" sx={{ fontWeight: 800 }}>
                      ABHA Number:{" "}
                    </Typography>
                    {p.abhaNumber}
                  </Typography>
                  <Typography sx={{ color: "var(--app-text)", fontSize: 14 }}>
                    <Typography component="span" sx={{ fontWeight: 800 }}>
                      Gender:{" "}
                    </Typography>
                    {p.gender === "M" ? "Male" : p.gender === "F" ? "Female" : p.gender}
                  </Typography>
                </Stack>
                <Icon icon="mdi:chevron-right" width={24} height={24} color="var(--app-text-muted)" />
              </Box>
            ))}
          </Stack>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "linkedAccounts" && selectedOption === "phone") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">Select Linked ABHA Profile</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div className="modal-body pt-0">
          <div className="mb-3">
            <p className="text-muted2 mb-2">Choose a profile linked with {maskedNumber}</p>
            <div className="border-0">
              {linkedProfiles?.map((p, i) => (
                <div key={i} className="mb-3">
                  <div
                    className="d-flex justify-content-between align-items-center px-3 py-3 rounded-3 bg-white"
                    style={{
                      border: "1px solid var(--app-border)",
                      boxShadow: "var(--app-shadow)",
                      cursor: "pointer",
                    }}
                    onClick={() => handleLinkedProfileContinue(p)}
                  >
                    <div className="text-start">
                      <div className="small mb-1">
                        <span className="fw-semibold">Name:&nbsp;</span>
                        <span>{p.name}</span>
                      </div>
                      <div className="small mb-1">
                        <span className="fw-semibold">ABHA Number:&nbsp;</span>
                        <span>{p.abhaNumber}</span>
                      </div>
                      <div className="small">
                        <span className="fw-semibold">Gender:&nbsp;</span>
                        <span>{p.gender === "M" ? "Male" : p.gender === "F" ? "Female" : p.gender}</span>
                      </div>
                    </div>

                    {/* Chevron icon */}
                    <Icon icon="mdi:chevron-right" width={24} height={24} />
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Step 5: ABHA Card
  if (currentStep === "abhaCard") {
    const detailRows = [
      {
        label: "Name:",
        value:
          [
            abhaProfile?.firstName,
            abhaProfile?.middleName,
            abhaProfile?.lastName,
          ]
            .filter(Boolean)
            .join(" ") ||
          abhaProfile?.name ||
          abhaProfile?.fullName ||
          "--",
      },
      {
        label: "Gender:",
        value:
          abhaProfile?.gender === "M"
            ? "Male"
            : abhaProfile?.gender === "F"
              ? "Female"
              : abhaProfile?.gender || "--",
      },
      {
        label: "Date of Birth:",
        value: abhaProfile?.userData ? abhaProfile?.userData?.dateOfBirth : abhaProfile?.dob,
      },
      {
        label: "Phone Number:",
        value: abhaProfile?.userData ? abhaProfile?.userData?.phoneNumber : abhaProfile?.mobile,
      },
      {
        label: "ABHA Number:",
        value: abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? "--",
        copy: abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? "",
      },
      {
        label: "ABHA Address:",
        value: abhaProfile?.userData
          ? abhaProfile?.userData?.abhaDetails?.abhaAddress
          : abhaProfile?.preferredAddress,
        copy: abhaProfile?.userData
          ? abhaProfile?.userData?.abhaDetails?.abhaAddress
          : abhaProfile?.preferredAddress,
      },
    ];

    return (
      <ModalShell title="ABHA Card" onBack={handleBack} onClose={onClose}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              position: "relative",
              borderRadius: "14px",
              overflow: "hidden",
              backgroundColor: "var(--app-surface-2)",
              border: "1px solid var(--app-border)",
              minHeight: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {abhaProfile?.abhaCardLink ? (
              <>
                <img
                  src={abhaProfile.abhaCardLink}
                  alt="ABHA Card"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <IconButton
                  onClick={handleDownloadAbhaCard}
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    backgroundColor: "var(--app-surface)",
                    color: "#5751E9",
                    boxShadow: "var(--app-shadow)",
                    "&:hover": { backgroundColor: "var(--app-hover)" },
                  }}
                >
                  <Icon icon="mdi:download" width={20} />
                </IconButton>
              </>
            ) : (
              <Skeleton variant="rectangular" width="100%" height={180} />
            )}
          </Box>
          <Box sx={{ ...cardSx, overflow: "hidden" }}>
            {detailRows.map((row, index) => (
              <Box key={row.label}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "130px 1fr", sm: "170px 1fr" },
                    gap: 1.5,
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography sx={{ color: "var(--app-text-muted)", fontSize: 14 }}>
                    {row.label}
                  </Typography>
                  {abhaProfile ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "var(--app-text)",
                          fontWeight: 800,
                          wordBreak: "break-word",
                        }}
                      >
                        {row.value || "--"}
                      </Typography>
                      {row.copy && (
                        <IconButton
                          size="small"
                          onClick={() => handleCopyToClipboard(row.copy)}
                          sx={{ color: "#5751E9" }}
                        >
                          <Icon icon="mi:copy" width={18} />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    <Skeleton width={140} />
                  )}
                </Box>
                {index < detailRows.length - 1 && <Divider sx={{ borderColor: "var(--app-border)" }} />}
              </Box>
            ))}
          </Box>
          <Button
            fullWidth
            onClick={() => {
              updateAbhaUser?.(abhaProfile || abhaProfile?.userData);
              onClose();
            }}
            sx={primaryButtonSx}
          >
            Done
          </Button>
        </Stack>
      </ModalShell>
    );
  }

  if (false && currentStep === "abhaCard") {
    return (
      <div className="">
        <div className="modal-header border-0 pb-0">
          <h5 className="modal-title fw-bold">ABHA Card</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        {/* <div className="modal-body pt-0">
          <div className="bg-light rounded-3 mb-4" style={{ position: "relative", overflow: "hidden" }}>
            {abhaProfile?.abhaCardLink && (
              <img
                src={abhaProfile?.abhaCardLink}
                alt="ABHA Card"
                style={{ width: "100%", display: "block" }}
              />
            )}
            {abhaProfile?.abhaCardLink && (
              <button
                type="button"
                onClick={handleDownloadAbhaCard}
                className="btn btn-light rounded-circle"
                style={{
                  position: "absolute",
                  right: "12px",
                  bottom: "12px",
                  width: "44px",
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}
              >
                <i className="fas fa-download"></i>
              </button>
            )}
          </div>

          <div className="mb-4">
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">Name</span>
              <span className="fw-bold">
                {abhaProfile?.name ?? abhaProfile?.fullName ?? "--"}
              </span>
            </div>
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">Gender</span>
              <span className="fw-bold">
                {abhaProfile?.gender === "M" ? "Male" : "Female"}
              </span>
            </div>
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">Date of Birth</span>
              <span className="fw-bold">
                {abhaProfile?.userData?.dateOfBirth}
              </span>
            </div>
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">Phone Number</span>
              <span className="fw-bold">
                {abhaProfile?.userData?.phoneNumber}
              </span>
            </div>
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">ABHA Number</span>
              <div className="d-flex align-items-center">
                <span className="fw-bold me-2">
                  {abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? "--"}
                </span>
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 border-0"
                  onClick={() =>
                    handleCopyToClipboard(
                      abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? ""
                    )
                  }
                  style={{ color: "#6c757d" }}
                >
                  <Icon
                    icon="mi:copy"
                    style={{ fontSize: "20px", color: "#5751e9" }}
                  />
                </button>
              </div>
            </div>
            <div className="border-bottom py-2 d-flex justify-content-between align-items-center">
              <span className="text-muted2">ABHA Address</span>
              <div className="d-flex align-items-center">
                <span className="fw-bold me-2">
                  {abhaProfile?.userData?.abhaDetails?.abhaAddress}
                </span>
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0 border-0"
                  onClick={() =>
                    handleCopyToClipboard(abhaProfile?.userData?.abhaDetails?.abhaAddress)
                  }
                  style={{ color: "#6c757d" }}
                >
                  <Icon
                    icon="mi:copy"
                    style={{ fontSize: "20px", color: "#5751e9" }}
                  />
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-added-primary w-100"
            onClick={() => {
              updateAbhaUser?.(abhaProfile?.userData);
              onClose();
            }}
          >
            Done
          </button>
        </div> */}
        <Box sx={{ px: 2, pb: 2 }}>
          {/* ABHA Card Image */}
          <Box
            sx={{
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              mb: 3,
              backgroundColor: "var(--app-surface-2)",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {abhaProfile?.abhaCardLink ? (
              <>
                <img
                  src={abhaProfile.abhaCardLink}
                  alt="ABHA Card"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />

                <IconButton
                  onClick={handleDownloadAbhaCard}
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    backgroundColor: "var(--app-surface)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": { backgroundColor: "var(--app-surface-2)" },
                  }}
                >
                  <Icon icon="mdi:download" width={20} />
                </IconButton>
              </>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )}
          </Box>

          {/* Details */}
          <Box>

            {/* Name */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                Name:
              </Typography>

              {abhaProfile ? (
                <Typography fontWeight={600}>
                  {[
                    abhaProfile?.firstName,
                    abhaProfile?.middleName,
                    abhaProfile?.lastName
                  ]
                    .filter(Boolean)
                    .join(' ') || '--'}
                </Typography>
              ) : (
                <Skeleton width={120} />
              )}
            </Box>
            <Divider />

            {/* Gender */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                Gender:
              </Typography>

              {abhaProfile ? (
                <Typography fontWeight={600}>
                  {abhaProfile?.gender === "M" ? "Male" : "Female"}
                </Typography>
              ) : (
                <Skeleton width={80} />
              )}
            </Box>
            <Divider />

            {/* DOB */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                Date of Birth:
              </Typography>

              {abhaProfile ? (
                <Typography fontWeight={600}>
                  {abhaProfile?.userData ? (
                    abhaProfile?.userData?.dateOfBirth
                  ) : (
                    abhaProfile?.dob
                  )}
                </Typography>
              ) : (
                <Skeleton width={120} />
              )}
            </Box>
            <Divider />

            {/* Phone */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                Phone Number:
              </Typography>

              {abhaProfile ? (
                <Typography fontWeight={600}>
                  {abhaProfile?.userData ? (
                    abhaProfile?.userData?.phoneNumber
                  ) : (abhaProfile?.mobile)}
                </Typography>
              ) : (
                <Skeleton width={140} />
              )}
            </Box>
            <Divider />

            {/* ABHA Number */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                ABHA Number:
              </Typography>

              {abhaProfile ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>
                    {abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? "--"}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      handleCopyToClipboard(
                        abhaProfile?.ABHANumber ?? abhaProfile?.abhaNumber ?? ""
                      )
                    }
                  >
                    <Icon icon="mi:copy" width={18} color="#5751e9" />
                  </IconButton>
                </Box>
              ) : (
                <Skeleton width={150} />
              )}
            </Box>
            <Divider />

            {/* ABHA Address */}
            <Box display="flex" py={1.5} alignItems="center">
              <Typography sx={{ width: 300, color: "var(--app-text-muted)", fontSize: 14 }}>
                ABHA Address:
              </Typography>

              {abhaProfile ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>
                    {abhaProfile?.userData
                      ? abhaProfile?.userData?.abhaDetails?.abhaAddress
                      : abhaProfile?.preferredAddress}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      handleCopyToClipboard(
                        abhaProfile?.userData
                          ? abhaProfile?.userData?.abhaDetails?.abhaAddress
                          : abhaProfile?.preferredAddress
                      )
                    }
                  >
                    <Icon icon="mi:copy" width={18} color="#5751e9" />
                  </IconButton>
                </Box>
              ) : (
                <Skeleton width={150} />
              )}
            </Box>

          </Box>

          {/* Done Button */}
          <Button
            fullWidth
            onClick={() => {
              updateAbhaUser?.(abhaProfile|| abhaProfile?.userData );
              onClose();
            }}
            sx={{
              mt: 3,
              height: 46,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(90deg,#5751E9,#6C63FF)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg,#4c46d9,#5f56ff)",
              },
            }}
          >
            Done
          </Button>
        </Box>
      </div>
    );
  }

  return null;
};

export default AbhaVerificationModal;
